// src/utils/ai.js
import axios from "axios";

axios.defaults.timeout = 12000;

/* ------------------------------------------------------------------ */
/* Helpers + keyword extraction                                       */
/* ------------------------------------------------------------------ */

/** Ensure we have an API key before making any OpenAI request. */
function ensureApiKey(key) {
  if (!key) {
    throw new Error(
      "OpenAI API key missing. Call AI.setKey('YOUR_KEY') first."
    );
  }
}

/** Normalize OpenAI error objects into a readable Error. */
function unwrapOpenAIError(err) {
  const msg =
    err?.response?.data?.error?.message ||
    err?.response?.data?.message ||
    err?.message ||
    String(err);
  return new Error(`OpenAI error: ${msg}`);
}

/** Common English stopwords, stored as a Set for O(1) lookup. */
const STOP_WORDS = new Set([
  "the","and","with","that","this","from","your","into","over","under","for","you",
  "are","was","were","will","can","could","should","would","have","has","had","about",
  "onto","than","then","also","such","like","when","what","how","why","where","which",
  "who","while","to","in","on","of","a","an","is","it","at","as","by","or","if","be",
  "we","they","them","their","our","us","he","she","him","her","his","hers","its",
  "up","down","off","out","so","no","not","just","only","very"
]);

/**
 * Tiny keyword helper.
 *
 * Extracts the most frequent non-stopword tokens from text.
 *
 * Backwards-compatible signatures:
 *   basicKeywords(text)                     -> top 10 keywords
 *   basicKeywords(text, 5)                  -> top 5 keywords
 *   basicKeywords(text, { limit: 15 })      -> top 15 keywords
 *   basicKeywords(text, { minLength: 4 })   -> words of len >= 4
 *
 * @param {string} text
 * @param {number|{limit?:number,minLength?:number}} [optionsOrLimit]
 * @returns {string[]} ordered list of keywords (most frequent first)
 */
export function basicKeywords(text = "", optionsOrLimit) {
  let limit = 10;
  let minLength = 3;

  if (typeof optionsOrLimit === "number") {
    limit = optionsOrLimit;
  } else if (optionsOrLimit && typeof optionsOrLimit === "object") {
    if (typeof optionsOrLimit.limit === "number") {
      limit = optionsOrLimit.limit;
    }
    if (typeof optionsOrLimit.minLength === "number") {
      minLength = optionsOrLimit.minLength;
    }
  }

  if (!text || typeof text !== "string") return [];

  // Lowercase and pull out "words" including simple hyphenated tokens.
  const matches = text.toLowerCase().match(/[a-z][a-z-]+/g);
  if (!matches) return [];

  const freq = Object.create(null);

  for (let raw of matches) {
    // Normalize some hyphen edge cases (e.g., "--word--" -> "word").
    const w = raw.replace(/^-+|-+$/g, "");
    if (!w || w.length < minLength) continue;
    if (STOP_WORDS.has(w)) continue;

    freq[w] = (freq[w] || 0) + 1;
  }

  return Object.keys(freq)
    .sort((a, b) => freq[b] - freq[a])
    .slice(0, limit);
}

/* ------------------------------------------------------------------ */
/* Image input parser                                                 */
/* ------------------------------------------------------------------ */

/**
 * Parse strings like:
 *   "prompt | size:1024x1024 | style:photorealistic | n:2 | quality:high"
 *
 * into a structured object for generateImage().
 *
 * Adds support for optional:
 *   model:my-model-name
 *   seed:12345
 *
 * Supports quality values:
 *   - Legacy: "standard", "hd"
 *   - New:    "low", "medium", "high"
 *
 * Internally normalizes to "low" | "medium" | "high".
 *
 * @param {string} input
 * @returns {{
 *   prompt: string,
 *   size: string,
 *   n: number,
 *   style: string,
 *   quality: "low"|"medium"|"high",
 *   model?: string,
 *   seed?: number
 * }}
 */
export function parseImageInput(input) {
  const parts = String(input || "")
    .split("|")
    .map(s => s.trim())
    .filter(Boolean);

  // Default tuned for image models
  const out = {
    prompt: "",
    size: "1024x1024",
    n: 1,
    style: "",
    quality: /** @type {"low"|"medium"|"high"} */ ("medium"),
    model: undefined,
    seed: undefined
  };

  if (!parts.length) return out;

  // First segment assumed prompt unless it looks like key:value
  if (!/^[a-z0-9_-]+\s*:/i.test(parts[0])) out.prompt = parts[0];

  // Other segments as key:value
  parts.forEach(seg => {
    const m = seg.match(/^([a-z]+)\s*:\s*(.+)$/i);
    if (!m) return;
    const key = m[1].toLowerCase();
    const val = m[2].trim();

    if (key === "size") {
      out.size = val;
    } else if (key === "n") {
      const n = parseInt(val, 10);
      out.n = Number.isFinite(n) ? Math.max(1, Math.min(4, n)) : 1;
    } else if (key === "style") {
      out.style = val;
    } else if (key === "quality") {
      // Normalize both legacy (standard/hd) and new low/medium/high
      if (/^hd$/i.test(val)) out.quality = "high";
      else if (/^standard$/i.test(val)) out.quality = "medium";
      else if (/^(low|medium|high)$/i.test(val)) {
        out.quality = /** @type {"low"|"medium"|"high"} */ (val.toLowerCase());
      }
    } else if (key === "model") {
      out.model = val;
    } else if (key === "seed") {
      const seed = parseInt(val, 10);
      if (Number.isFinite(seed)) out.seed = seed;
    }
  });

  return out;
}

/* ------------------------------------------------------------------ */
/* OpenAI wrapper                                                     */
/* ------------------------------------------------------------------ */

/**
 * @typedef {Object} ChatOptions
 * @property {string} [model]       - override default chat model
 * @property {number} [temperature] - sampling temperature (0–2)
 * @property {number} [max_tokens]  - optional max_tokens
 * @property {Object} [extra]       - any additional parameters to pass through
 */

/**
 * @typedef {Object} ImageParams
 * @property {string} prompt
 * @property {string} [size]
 * @property {number} [n]
 * @property {"low"|"medium"|"high"} [quality]
 * @property {string} [style]
 * @property {string} [model]
 * @property {number} [seed]
 */

export const AI = {
  /**
   * IMPORTANT: Do NOT hard-code real keys in source.
   * Set this at runtime: AI.setKey(import.meta.env.VITE_OPENAI_KEY) or similar.
   */
  gptApiKey: "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",

  // Default to GPT-5.1 (reasoning model) via Chat Completions.
  // You can override per-call or via AI.setModel().
  model: "gpt-5-chat-latest",

  setKey(key) {
    this.gptApiKey = key;
  },

  setModel(m) {
    this.model = m;
  },

  /**
   * Low-level wrapper around OpenAI Chat Completions API.
   * Accepts either an array of {role,content} OR (system, userPrompt).
   *
   * @param {Array<{role:"system"|"user"|"assistant",content:string}>|String} systemOrMessages - messages[] OR system string
   * @param {String|null} userPrompt        - user content if first arg is system
   * @param {String|ChatOptions|null} model - model name or options { model, temperature, ... }
   *
   * @returns {Promise<string>} assistant message text
   */
  async chatComplete(systemOrMessages, userPrompt = null, model = null) {
    ensureApiKey(this.gptApiKey);

    let useModel = this.model || "gpt-5-chat-latest";
    let temperature = 0.2;
    let extraParams = {};

    // Allow model to be either a string or an options object
    if (typeof model === "string" && model) {
      useModel = model;
    } else if (model && typeof model === "object") {
      if (typeof model.model === "string") useModel = model.model;
      if (typeof model.temperature === "number") temperature = model.temperature;

      // Copy and remove known keys to pass the rest straight through
      extraParams = { ...model };
      delete extraParams.model;
      delete extraParams.temperature;
    }

    const messages = Array.isArray(systemOrMessages)
      ? systemOrMessages.map(m => ({
          role: m.role,
          content: String(m.content || "")
        }))
      : [
          { role: "system", content: String(systemOrMessages || "") },
          { role: "user", content: String(userPrompt || "") }
        ];

    try {
      const res = await axios.post(
        "https://api.openai.com/v1/chat/completions",
        {
          model: useModel,
          messages,
          temperature,
          ...extraParams
        },
        {
          headers: {
            Authorization: `Bearer ${this.gptApiKey}`,
            "Content-Type": "application/json"
          }
        }
      );

      const text = String(res.data?.choices?.[0]?.message?.content || "").trim();
      return text;
    } catch (err) {
      throw unwrapOpenAIError(err);
    }
  },

  /**
   * Call Images API using DALL·E 3 (dall-e-3) by default.
   *
   * @param {ImageParams} params { prompt, size, n, quality, style, model? }
   * @returns {Promise<{ urls: string[], raw: any }>}
   */
  async generateImage(params) {
    ensureApiKey(this.gptApiKey);

    const {
      prompt,
      size = "1024x1024",
      n = 1,
      // We accept "low" | "medium" | "high" or "standard" | "hd"
      // and normalize to "standard" | "hd" for DALL·E 3.
      quality = "standard",
      style = "",
      model = "dall-e-3",
    } = params || {};

    if (!prompt) throw new Error("Image prompt is required.");

    // Fold style into prompt so the model honors it
    const fullPrompt = style ? `${prompt}\n\nStyle: ${style}` : prompt;

    // Normalize quality:
    // - support our newer "low/medium/high" or older "standard/hd" inputs
    let qualityNorm = quality;
    if (/^low$/i.test(quality)) qualityNorm = "standard";   // no real low mode, map to standard
    else if (/^medium$/i.test(quality)) qualityNorm = "standard";
    else if (/^high$/i.test(quality)) qualityNorm = "hd";
    else if (!/^(standard|hd)$/i.test(qualityNorm)) qualityNorm = "standard";

    const body = {
      model,
      prompt: fullPrompt,
      n,
      size,                 // "1024x1024", "1024x576", "1792x1024", etc.
      quality: qualityNorm, // "standard" | "hd"
      response_format: "url"
    };

    try {
      const res = await axios.post(
        "https://api.openai.com/v1/images/generations",
        body,
        {
          headers: {
            Authorization: `Bearer ${this.gptApiKey}`,
            "Content-Type": "application/json"
          },
          timeout: 120000
        }
      );

      const urls = (res.data?.data || [])
        .map(item => item?.url)
        .filter(Boolean);

      if (!urls.length) throw new Error("Image generation returned no URLs.");
      return { urls, raw: res.data };
    } catch (err) {
      // Reuse the same normalized error shape used everywhere else
      throw unwrapOpenAIError(err);
    }
  },

  /** High-level helpers used by AIDashboard.vue */

  /**
   * Simple chat helper: returns { text, keywords }.
   *
   * Matches usages like:
   *   await AI.chat("Hello world")
   *   await AI.chat(prompt, { temperature: 0.2, max_tokens: 900 })
   *   await AI.chat(prompt, "gpt-4o")
   *
   * @param {string} userPrompt
   * @param {string|ChatOptions} [optsOrModel] - optional model name or options
   * @returns {Promise<{text: string, keywords: string[]}>}
   */
  async chat(userPrompt, optsOrModel = null) {
    const sys =
      "You are COGNEETIV (Enhanced AI). Be concise, helpful, and structured when appropriate. Use markdown headings and bullets when it improves readability.";

    let options = {};

    if (typeof optsOrModel === "string") {
      options = { model: optsOrModel };
    } else if (optsOrModel && typeof optsOrModel === "object") {
      options = { ...optsOrModel };
    }

    // Default temperature if caller didn't specify one
    if (typeof options.temperature !== "number") {
      options.temperature = 0.3;
    }

    const text = await this.chatComplete(sys, userPrompt, options);
    return { text, keywords: basicKeywords(text) };
  },

  /**
   * IMAGE: Creates images and returns their URLs.
   * Accepts either a raw prompt or:
   *   "prompt | size:WxH | style:... | quality:high | n:2 | model:... | seed:123"
   *
   * @param {string} userPrompt
   * @returns {Promise<{text: string, imageUrls: string[], keywords: string[]}>}
   */
  async image(userPrompt) {
    const parsed = parseImageInput(userPrompt);
    const finalPrompt = parsed.prompt;

    const { urls } = await this.generateImage({
      prompt: finalPrompt,
      size: parsed.size,
      n: parsed.n,
      quality: parsed.quality,
      style: parsed.style,
      model: parsed.model
      // seed intentionally not used here with DALL·E 3
    });

    const text = urls.length === 1
      ? `✅ Image generated:\n${urls[0]}`
      : `✅ ${urls.length} images generated:\n${urls.join("\n")}`;

    return {
      text,
      imageUrls: urls,
      keywords: basicKeywords(finalPrompt)
    };
  },

  /**
   * Design short video concepts.
   * @param {string} userPrompt
   * @returns {Promise<{text: string, keywords: string[]}>}
   */
  async video(userPrompt) {
    const sys = "You design short video concepts. Return: Title, Logline, Shot list, Visual style, Duration.";
    const text = await this.chatComplete(sys, userPrompt, {
      temperature: 0.4
    });
    return { text, keywords: basicKeywords(text) };
  },

  /**
   * Create a compact storyboard plan.
   * @param {string} userPrompt
   * @returns {Promise<{text: string, keywords: string[]}>}
   */
  async storyboard(userPrompt) {
    const sys = "You create a compact storyboard plan. Return numbered frames with scene, action, camera, notes.";
    const text = await this.chatComplete(sys, userPrompt, {
      temperature: 0.4
    });
    return { text, keywords: basicKeywords(text) };
  },
};

export default AI;
